import React, { useCallback, useReducer, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    MenuItem,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import { InsuranceProviderService } from '../../service/insuranceProviderService';
import { toast } from 'react-toastify';
import { useQuery, useMutation, useQueryClient } from 'react-query';

/* ──────────────────────────────────────────────────────── */
/*  Provider Form Dialog (create + edit)                   */
/* ──────────────────────────────────────────────────────── */
function ProviderFormDialog({ open, onClose, provider }) {
    const [name, setName] = useState(provider?.name ?? '');
    const [status, setStatus] = useState(provider?.status ?? 'active');
    const [submitting, setSubmitting] = useState(false);
    const queryClient = useQueryClient();

    const isEdit = Boolean(provider?.id);

    const handleSubmit = async () => {
        const trimmed = name.trim();
        if (!trimmed) return;

        setSubmitting(true);
        try {
            if (isEdit) {
                await InsuranceProviderService.update(provider.id, { name: trimmed, status });
                toast.success('Provider updated');
            } else {
                await InsuranceProviderService.create({ name: trimmed });
                toast.success('Provider created');
            }
            queryClient.invalidateQueries(['insuranceProviders']);
            onClose();
        } catch (err) {
            // the httpservice already throws user-friendly messages for known error cases (e.g. duplicate name)
            // toast.error(err?.message ?? 'An error occurred');
        } finally {
            setSubmitting(false);
        }
    };

    // Reset when dialog opens with new data
    React.useEffect(() => {
        if (open) {
            setName(provider?.name ?? '');
            setStatus(provider?.status ?? 'active');
        }
    }, [open, provider]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{isEdit ? 'Edit Provider' : 'Add Provider'}</DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Provider Name"
                            required
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            autoFocus
                        />
                    </Grid>
                    {isEdit && (
                        <Grid item xs={12}>
                            <TextField
                                select
                                fullWidth
                                label="Status"
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                            >
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="inactive">Inactive</MenuItem>
                            </TextField>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions sx={{ boxShadow: 4 }}>
                <Button variant="outlined" onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!name.trim() || submitting}
                >
                    {isEdit ? 'Update' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/* ──────────────────────────────────────────────────────── */
/*  Main Insurance Providers Admin Screen                  */
/* ──────────────────────────────────────────────────────── */
export default function InsuranceProviders() {
    const queryClient = useQueryClient();
    const [formDialog, setFormDialog] = useState({ open: false, provider: null });

    const { data: providersResult, isLoading } = useQuery(
        ['insuranceProviders'],
        () => InsuranceProviderService.getAll(),
        { select: (r) => r?.data ?? [] }
    );

    const providers = providersResult ?? [];

    const handleDeactivate = async (provider) => {
        try {
            await InsuranceProviderService.deactivate(provider.id);
            toast.success(`"${provider.name}" deactivated`);
            queryClient.invalidateQueries(['insuranceProviders']);
        } catch (err) {
            toast.error(err?.message ?? 'An error occurred');
        }
    };

    const openCreate = () => setFormDialog({ open: true, provider: null });
    const openEdit = (p) => setFormDialog({ open: true, provider: p });
    const closeForm = () => setFormDialog({ open: false, provider: null });

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                <Typography variant="h5" fontWeight={600} sx={{ flexGrow: 1 }}>
                    Insurance Providers
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={openCreate}
                >
                    Add Provider
                </Button>
            </Box>

            {isLoading ? (
                <Typography color="text.secondary">Loading…</Typography>
            ) : providers.length === 0 ? (
                <Typography color="text.secondary">No providers found. Add one to get started.</Typography>
            ) : (
                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                    <Box component="thead">
                        <Box component="tr" sx={{ borderBottom: '2px solid', borderColor: 'divider' }}>
                            {['Provider Name', 'Status', 'Scope', ''].map((h) => (
                                <Box
                                    key={h}
                                    component="th"
                                    sx={{ textAlign: 'left', p: 1.5, fontWeight: 600, fontSize: 14 }}
                                >
                                    {h}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                    <Box component="tbody">
                        {providers.map((p) => (
                            <Box
                                key={p.id}
                                component="tr"
                                sx={{
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    '&:hover': { bgcolor: 'action.hover' },
                                    opacity: p.status === 'inactive' ? 0.5 : 1,
                                }}
                            >
                                <Box component="td" sx={{ p: 1.5 }}>
                                    <Typography>{p.name}</Typography>
                                </Box>
                                <Box component="td" sx={{ p: 1.5 }}>
                                    <Chip
                                        label={p.status}
                                        size="small"
                                        color={p.status === 'active' ? 'success' : 'default'}
                                    />
                                </Box>
                                <Box component="td" sx={{ p: 1.5 }}>
                                    <Chip
                                        label={p.isGlobal ? 'Global' : 'Tenant'}
                                        size="small"
                                        variant="outlined"
                                    />
                                </Box>
                                <Box component="td" sx={{ p: 1.5, textAlign: 'right' }}>
                                    {!p.isGlobal && (
                                        <>
                                            <Tooltip title="Edit">
                                                <IconButton size="small" onClick={() => openEdit(p)}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            {p.status === 'active' && (
                                                <Tooltip title="Deactivate">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDeactivate(p)}
                                                    >
                                                        <BlockIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </>
                                    )}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            <ProviderFormDialog
                open={formDialog.open}
                onClose={closeForm}
                provider={formDialog.provider}
            />
        </Box>
    );
}
